export interface LocalDeliveryOption extends LocalDeliveryOptionConfigOneOf {
  zipConfig?: ZipCodeConfig;
  radiusConfig?: RadiusConfig;
  customAreaConfig?: CustomAreaConfig;
  cityConfig?: CityConfig;
  /**
   * LocalDeliveryOptionId - unique identifier of local delivery option
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Area name of Local Delivery Option
   * @maxLength 100
   */
  areaName?: string | null;
  /**
   * Flat price of local delivery
   * @format DECIMAL_VALUE
   * @deprecated Flat price of local delivery
   * @targetRemovalDate 2025-01-12
   */
  rate?: string | null;
  /**
   * Estimated delivery time description text
   * @maxLength 40
   */
  estimatedDeliveryTime?: string | null;
  /**
   * Minimum order total to be eligible for free delivery
   * @deprecated Minimum order total to be eligible for free delivery
   * @targetRemovalDate 2025-01-12
   */
  minimumFreeDeliveryOrderAmount?: number | null;
  /**
   * List of countries with subdivisions
   * @maxSize 100
   */
  destinations?: Destination[];
  /**
   * Database revision of LocalDeliveryOption entity. Incremented on each modification
   * @readonly
   */
  revision?: string | null;
  scheduledDeliveryConfig?: ScheduledDelivery;
  /**
   * at runtime for a given shipment input, up to one rate (price) should be returned in an option. If more than one rate is valid then we return the lowest one.
   * @maxSize 50
   */
  rates?: ConditionalRates[];
  /**
   * Date and time the LocalDeliveryOption was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the LocalDeliveryOption was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * This local delivery is active for the following delivery regions.
   * @maxSize 50
   * @format GUID
   */
  deliveryRegionIds?: string[];
}

/** @oneof */
export interface LocalDeliveryOptionConfigOneOf {
  zipConfig?: ZipCodeConfig;
  radiusConfig?: RadiusConfig;
  customAreaConfig?: CustomAreaConfig;
  cityConfig?: CityConfig;
}

export interface Destination {
  /**
   * Country ISO code
   * @format COUNTRY
   */
  code?: string;
  /**
   * List of subdivisions, empty means all
   * @maxSize 200
   */
  subdivisions?: string[];
}

export interface ZipCodeConfig {
  /**
   * List of ZipCodes and ZipCode regexes. Examples: 10001, 10002 or 1000*
   * @minLength 1
   * @maxLength 300
   */
  zipCodes?: string[];
}

export interface RadiusConfig {
  /** Delivery-from address */
  address?: LocalDeliveryAddress;
  /**
   * Delivery circle's radius
   * @min 0.1
   * @max 50
   */
  radius?: number;
  /**
   * Radius' unit, taken from site properties
   * @readonly
   */
  lengthUnit?: LengthUnitWithLiterals;
}

export interface LocalDeliveryAddress {
  /**
   * A string containing the human-readable address of this location
   * @maxLength 400
   */
  formattedAddress?: string | null;
  /** coordinates of the physical address */
  geocode?: AddressLocation;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export enum LengthUnit {
  /** Length unit can't be classified, due to an error */
  UNSPECIFIED_LENGTH_UNIT = 'UNSPECIFIED_LENGTH_UNIT',
  /** Kilometers */
  KM = 'KM',
  /** Miles */
  MI = 'MI',
}

/** @enumType */
export type LengthUnitWithLiterals =
  | LengthUnit
  | 'UNSPECIFIED_LENGTH_UNIT'
  | 'KM'
  | 'MI';

export interface CustomAreaConfig {
  /**
   * Geocodes of the polygon defining the delivery area.
   * @maxSize 300
   */
  geocodes?: AddressLocation[];
}

export interface CityConfig {
  /**
   * List of the cities for delivery
   * @minSize 1
   * @maxSize 50
   */
  cityItems?: CityItem[];
}

export interface CityItem {
  /**
   * Name of the city
   * @minLength 1
   * @maxLength 100
   */
  cityName?: string;
  /**
   * Subdivision code of the city
   * @format SUBDIVISION
   */
  subdivision?: string | null;
  /**
   * Country code of the city
   * @format COUNTRY
   */
  country?: string;
  /**
   * Unique code of the city, calculated by Wix
   * @maxLength 100
   * @readonly
   */
  cityKey?: string | null;
}

export interface ScheduledDelivery {
  /**
   * Days config which contain days when delivery is enabled with it's time slots
   * @deprecated Days config which contain days when delivery is enabled with it's time slots
   * @replacedBy time_windows
   * @targetRemovalDate 2025-01-01
   */
  days?: Days;
  /**
   * Amount of days before first delivery option date
   * @max 30
   */
  handlingDays?: number | null;
  /** Cut off time after which first delivery option would be for next day */
  cutOffTime?: TimePoint;
  /**
   * availability times configuration.
   * @maxSize 100
   */
  timeWindows?: TimeWindow[];
}

export interface Days {
  monday?: DaySlot;
  tuesday?: DaySlot;
  wednesday?: DaySlot;
  thursday?: DaySlot;
  friday?: DaySlot;
  saturday?: DaySlot;
  sunday?: DaySlot;
}

export interface DaySlot {
  /** Shows whether this day slot is enabled for delivery */
  enabledForDelivery?: boolean | null;
  /**
   * Time slots for scheduled delivery during day
   * @maxSize 8
   */
  timeSlots?: TimeSlot[];
}

export interface TimeSlot {
  /** Starting time point of time slot */
  from?: TimePoint;
  /** End time point of time slot */
  to?: TimePoint;
}

export interface TimePoint {
  /**
   * Hours in format 0-23
   * @max 23
   */
  hours?: number | null;
  /**
   * Minutes 0-59
   * @max 59
   */
  minutes?: number | null;
}

export interface TimeWindow {
  /** The day of week this availability relates to. */
  day?: DayOfWeekWithLiterals;
  /** The start time in time of day representation. */
  start?: TimeOfDay;
  /** The end time in time of day representation. */
  end?: TimeOfDay;
  /** Whether this timeWindow availability is enabled. */
  enabled?: boolean | null;
}

export enum DayOfWeek {
  /** Monday. */
  MON = 'MON',
  /** Tuesday. */
  TUE = 'TUE',
  /** Wednesday. */
  WED = 'WED',
  /** Thursday. */
  THU = 'THU',
  /** Friday. */
  FRI = 'FRI',
  /** Saturday. */
  SAT = 'SAT',
  /** Sunday. */
  SUN = 'SUN',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'MON'
  | 'TUE'
  | 'WED'
  | 'THU'
  | 'FRI'
  | 'SAT'
  | 'SUN';

export interface TimeOfDay {
  /**
   * Hours
   * @max 23
   */
  hours?: number;
  /**
   * Minutes
   * @max 59
   */
  minutes?: number;
}

export interface ConditionalRates {
  /**
   * there is an AND logic between all the conditions. Empty conditions means true.
   * The amount of the rate that will be returned if all conditions are met.
   * @decimalValue options { gte:0, lte:999999999, maxScale:3 }
   */
  amount?: string;
  /**
   * For example: weight > 0 and weight <= 10
   * @maxSize 10
   */
  conditions?: Condition[];
  /** When this flag is set to true, multiply the amount by the number of line items passed on the request. */
  multiplyByQuantity?: boolean;
}

export interface Condition {
  type?: ConditionTypeWithLiterals;
  /**
   * The value in respective to the condition type
   * Weight values should be in the same weight units of the store: KG / LB
   * Total price is according to the store currency
   * Quantity of items should be integers
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  value?: string;
  /** Logical operator. */
  operator?: LogicalOperatorWithLiterals;
}

export enum ConditionType {
  BY_TOTAL_WEIGHT = 'BY_TOTAL_WEIGHT',
  BY_TOTAL_PRICE = 'BY_TOTAL_PRICE',
  BY_TOTAL_QUANTITY = 'BY_TOTAL_QUANTITY',
}

/** @enumType */
export type ConditionTypeWithLiterals =
  | ConditionType
  | 'BY_TOTAL_WEIGHT'
  | 'BY_TOTAL_PRICE'
  | 'BY_TOTAL_QUANTITY';

export enum LogicalOperator {
  EQ = 'EQ',
  GT = 'GT',
  GTE = 'GTE',
  LT = 'LT',
  LTE = 'LTE',
}

/** @enumType */
export type LogicalOperatorWithLiterals =
  | LogicalOperator
  | 'EQ'
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE';

export interface CreateLocalDeliveryOptionRequest {
  localDeliveryOption: LocalDeliveryOption;
}

export interface CreateLocalDeliveryOptionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface GetLocalDeliveryOptionRequest {
  /**
   * @maxLength 50
   * @deprecated
   * @targetRemovalDate 2025-01-12
   */
  id?: string;
  /** @maxLength 50 */
  localDeliveryOptionId: string;
}

export interface GetLocalDeliveryOptionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface ListLocalDeliveryOptionsRequest {
  /** @format GUID */
  externalId: string;
}

export interface ListLocalDeliveryOptionsResponse {
  localDeliveryOptions?: LocalDeliveryOption[];
}

export interface QueryLocalDeliveryOptionsRequest {
  /** WQL expression */
  query: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 5
   */
  sort?: Sorting[];
  /**
   * Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned.
   * @maxSize 100
   * @maxLength 20
   */
  fields?: string[];
  /**
   * Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned.
   * @maxSize 100
   * @maxLength 20
   */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryLocalDeliveryOptionsResponse {
  /** Retrieved local delivery options. */
  localDeliveryOptions?: LocalDeliveryOption[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpdateLocalDeliveryOptionRequest {
  /** LocalDeliveryOption to be updated, may be partial. */
  localDeliveryOption: LocalDeliveryOption;
}

export interface UpdateLocalDeliveryOptionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface DeleteLocalDeliveryOptionRequest {
  /**
   * @maxLength 50
   * @deprecated
   * @targetRemovalDate 2025-01-12
   */
  id?: string;
  /** @maxLength 50 */
  localDeliveryOptionId: string;
}

export interface DeleteLocalDeliveryOptionResponse {}

export interface BulkCreateLocalDeliveryOptionRequest {
  /**
   * @minSize 1
   * @maxSize 100
   */
  localDeliveryOptions: LocalDeliveryOption[];
}

export interface BulkCreateLocalDeliveryOptionResponse {
  localDeliveryOptions?: LocalDeliveryOption[];
  errors?: LocalDeliveryOptionError[];
}

export interface LocalDeliveryOptionError {
  id?: string;
  error?: string;
}

export interface BulkUpdateLocalDeliveryOptionRequest {
  /**
   * @minSize 1
   * @maxSize 100
   */
  localDeliveryOptions: LocalDeliveryOption[];
}

export interface BulkUpdateLocalDeliveryOptionResponse {
  localDeliveryOptions?: LocalDeliveryOption[];
  errors?: LocalDeliveryOptionError[];
}

export interface BulkDeleteLocalDeliveryOptionRequest {
  /**
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  ids: string[];
}

export interface BulkDeleteLocalDeliveryOptionResponse {}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface AddDeliveryRegionRequest {
  /** @format GUID */
  deliveryRegionId: string;
  /** @format GUID */
  localDeliveryOptionId: string;
  /** Revision of the PickupLocation */
  revision?: string | null;
}

export interface AddDeliveryRegionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface RemoveDeliveryRegionRequest {
  /** @format GUID */
  deliveryRegionId: string;
  /** @format GUID */
  localDeliveryOptionId: string;
  /** Revision of the PickupLocation */
  revision?: string | null;
}

export interface RemoveDeliveryRegionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateLocalDeliveryOptionApplicationErrors = {
  code?: 'LIMIT_OF_OPTIONS_PER_EXTERNAL_ID_EXCEEDED';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkCreateLocalDeliveryOptionApplicationErrors = {
  code?: 'LIMIT_OF_OPTIONS_PER_DELIVERY_REGION_EXCEEDED';
  description?: string;
  data?: Record<string, any>;
};
